import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalIcon } from "lucide-react";
import { adToBs, bsToAd, BS_YEARS, BS_MONTHS_LIST, bsMonthDays } from "@/lib/bsDate";

/**
 * Dual AD + BS date picker.
 * value: ISO date string (YYYY-MM-DD) — AD canonical.
 * onChange: (adIso) => void
 */
export default function DualDatePicker({ value, onChange, label, testId }) {
  const [open, setOpen] = useState(false);
  const bs = value ? adToBs(value) : null;

  const handleAdSelect = (d) => {
    if (!d) return;
    const iso = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString().slice(0, 10);
    onChange(iso);
    setOpen(false);
  };

  const handleBsChange = (y, m, day) => {
    const iso = bsToAd(parseInt(y, 10), parseInt(m, 10), parseInt(day, 10));
    if (iso) onChange(iso);
  };

  const currentBs = bs || { year: 2081, month: 1, day: 1 };
  const maxDay = bsMonthDays(currentBs.year, currentBs.month);

  return (
    <div className="space-y-1">
      {label && <Label className="text-xs font-medium uppercase tracking-wide text-slate-600">{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start text-left font-normal h-9"
            data-testid={testId || "date-picker-trigger"}
          >
            <CalIcon size={14} className="mr-2 text-slate-500" />
            {value ? (
              <span className="flex items-center gap-2">
                <span className="text-slate-900">{value}</span>
                {bs && <span className="text-xs text-slate-500">BS {bs.label}</span>}
              </span>
            ) : (
              <span className="text-slate-400">Select date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">AD Date</div>
            <Calendar
              mode="single"
              selected={value ? new Date(value + "T00:00:00") : undefined}
              onSelect={handleAdSelect}
            />
            <div className="border-t pt-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">BS Date (Bikram Sambat)</div>
              <div className="grid grid-cols-3 gap-2">
                <select
                  className="border border-slate-200 rounded-md text-sm px-2 py-1 bg-white"
                  value={currentBs.year}
                  onChange={(e) => handleBsChange(e.target.value, currentBs.month, currentBs.day)}
                  data-testid="bs-year-select"
                >
                  {BS_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <select
                  className="border border-slate-200 rounded-md text-sm px-2 py-1 bg-white"
                  value={currentBs.month}
                  onChange={(e) => handleBsChange(currentBs.year, e.target.value, currentBs.day)}
                  data-testid="bs-month-select"
                >
                  {BS_MONTHS_LIST.map((m, i) => <option key={m} value={i+1}>{m}</option>)}
                </select>
                <Input
                  type="number"
                  min={1}
                  max={maxDay}
                  value={currentBs.day}
                  onChange={(e) => handleBsChange(currentBs.year, currentBs.month, e.target.value)}
                  className="h-8 text-sm"
                  data-testid="bs-day-input"
                />
              </div>
              {bs && (
                <div className="text-xs text-slate-500 mt-2">
                  BS: {bs.year}-{String(bs.month).padStart(2,"0")}-{String(bs.day).padStart(2,"0")}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
